import { Injectable } from '@angular/core';
import { SectionType } from 'enums-lib';
import { ISection, IWorkflowConfig, Section } from 'interfaces-lib';
import { MessageNotificationService } from 'message-notification-service-lib';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, finalize, map, tap } from 'rxjs/operators';
import { WorkflowConfigDataService } from './data/workflow-config-data.service';
import { UiService } from './ui.service';

export enum WorkflowConfigUpdateHint {
  CREATED,
  UPDATED,
  DELETED,
}

export type WorkflowConfigUpdateEvent = {
  hint: WorkflowConfigUpdateHint;
  workflowConfig: IWorkflowConfig;
};

interface ISectionFile {
  id: string;
  order: number;
  file: File;
}

/**
 * Service that works with workflow-config.component to maintain a single IWorkflowConfig record. The Garry Service.
 * This is an alternative to having the workflow-config-list-view.component handle the workflow-config (dialog) directly.
 * This service is also responsible for updating the ui state and showing messages.
 */
@Injectable({ providedIn: 'root' })
export class WorkflowConfigService {
  private workflowConfigUpdateSubject = new Subject<WorkflowConfigUpdateEvent>();
  public workflowConfigUpdate$ = this.workflowConfigUpdateSubject.asObservable();

  constructor(
    private workflowConfigDataService: WorkflowConfigDataService,
    private messageNotificationService: MessageNotificationService,
    private uiService: UiService
  ) {
    console.log('WorkflowConfigService created.');
  }

  save(workflowConfig: Readonly<IWorkflowConfig>): Observable<IWorkflowConfig> {
    console.log('Saving', workflowConfig);

    const sectionsWithFiles: ISectionFile[] = this.getSectionsWithFiles(workflowConfig);
    console.log('Files to upload', sectionsWithFiles);

    this.uiService.save();
    const newRecord = !workflowConfig.id;
    const obs$ = newRecord ? this.workflowConfigDataService.create(workflowConfig) : this.workflowConfigDataService.update(workflowConfig);
    return obs$.pipe(
      tap((wfcResp) => this.updateFileSectionIds(sectionsWithFiles, wfcResp.sections)),
      concatMap((wfcResp) =>
        this.uploadSectionFiles(wfcResp.id, sectionsWithFiles).pipe(
          tap((fdrArray) => console.log(`${sectionsWithFiles.length} out of ${fdrArray.length} files uploaded.`)),
          map((fdrArray) => wfcResp) // map back to the original response so the rest of the pipe can use it
        )
      ),
      map((resp) => ({
        hint: newRecord ? WorkflowConfigUpdateHint.CREATED : WorkflowConfigUpdateHint.UPDATED,
        workflowConfig: resp,
      })),
      tap((resp) => {
        resp.workflowConfig.dateCreated = new Date((resp.workflowConfig.dateCreated as number) * 1000);
        resp.workflowConfig.dateModified = new Date((resp.workflowConfig.dateModified as number) * 1000);

        this.workflowConfigUpdateSubject.next(resp);
      }),
      map((resp) => resp.workflowConfig),
      finalize(() => this.uiService.saved())
    );
  }

  delete(workflowConfig: Readonly<IWorkflowConfig>): Observable<void> {
    console.log('Deleting', workflowConfig);
    return this.workflowConfigDataService.delete(workflowConfig.id).pipe(
      map((resp) => ({
        hint: WorkflowConfigUpdateHint.DELETED,
        workflowConfig: workflowConfig,
      })),
      tap((resp) => this.workflowConfigUpdateSubject.next(resp)),
      tap((resp) => this.messageNotificationService.success(`Successfully deleted '${workflowConfig.name}'`)),
      map((resp) => undefined),
      catchError((err) => {
        this.messageNotificationService.error(`There was a problem deleting '${workflowConfig.name}'.`, err);
        return err;
      })
    );
  }

  private getSectionsWithFiles(workflowConfig: IWorkflowConfig): ISectionFile[] {
    const sectionsWithFiles = [];

    workflowConfig.sections.forEach((section) => {
      if (section.type === SectionType.DocxTemplate ||
        section.type === SectionType.HtmlDocxTemplate ||
        section.type === SectionType.FixedFile
      ) {
        const file: File = section['file'];
        if (file) {
          sectionsWithFiles.push({
            id: section.id,
            order: section.order,
            file: file,
          });
        }
      }
    });

    return sectionsWithFiles;
  }

  private updateFileSectionIds(targetSections: ISectionFile[], sourceSections: Section[]): void {
    targetSections.forEach((target) => {
      // since the target id could be null (i.e. new) then we can only rely on the order to match to the source
      const source = sourceSections.find((ss) => ss.order === target.order);
      if (!source) {
        throw new Error('Failed to find source section.');
      }
      if (target.id && target.id !== source.id) {
        throw new Error('Section mismatch.');
      }
      target.id = source.id;
    });
  }

  private uploadSectionFiles(workflowConfigId: string, sections: ISectionFile[]): Observable<any[]> {
    const fileUpload$ = [];

    sections.forEach((section) => {
      fileUpload$.push(
        this.workflowConfigDataService
          .uploadSectionFile(workflowConfigId, section.id, section.file)
          .pipe(tap((_) => console.log(`Uploading file '${section.file.name}' to section '${section.id}'.`)))
      );
    });

    if (fileUpload$.length === 0) {
      return of([]);
    }

    return forkJoin(fileUpload$);
  }
}
